import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { keepPreviousForCompany } from '@/lib/company-placeholder';

import { useApi } from '@/lib/api-context';

import type { QueryParams } from './use-url-filters';
import { masterListResponseSchema, type MasterListResponse } from './types';

/** Todos los filtros de la vista viajan al backend: se recorren las claves en vez de
 *  enumerarlas a mano (así se habían perdido cláusula, tipo, proceso y por firmar,
 *  QA F1 #12). Lo vacío no se manda. */
export function toSearch(params: Partial<QueryParams>): string {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params) as [string, string | number | undefined][]) {
    if (value !== undefined && value !== '') {
      qs.set(key, String(value));
    }
  }
  return qs.toString();
}

/** Filtros para exportar (spec 2026-10-14 §3): los mismos del listado, sin paginar. */
export function exportSearch(params: QueryParams): string {
  return toSearch({ ...params, page: undefined, limit: undefined });
}

interface Options {
  /** Empresa activa: entra al queryKey para no servir datos cacheados de otra
   *  empresa al cambiar (keepPreviousData). */
  companyId: string | null;
  /** No dispara hasta conocer la empresa activa (garantiza el header
   *  X-Active-Company en carga fría/deep-link). */
  enabled: boolean;
}

/** Listado enriquecido de la lista maestra de la empresa activa. Mantiene la
 *  página previa mientras carga la nueva (sin parpadeo al filtrar/paginar).
 *  Valida el contrato de respuesta con Zod: un drift del backend cae al estado
 *  de error de la query, no a un throw en render (nunca pantalla blanca). */
export function useMasterList(
  params: QueryParams,
  { companyId, enabled }: Options,
): UseQueryResult<MasterListResponse> {
  const api = useApi();
  return useQuery({
    queryKey: ['master-list', companyId, params],
    queryFn: async () => {
      const raw = await api.get<unknown>(`/api/v1/quality/master-list?${toSearch(params)}`);
      return masterListResponseSchema.parse(raw);
    },
    enabled,
    placeholderData: keepPreviousForCompany<MasterListResponse>(companyId),
  });
}
